import * as React from 'react';
import * as styles from './IconPicker.css';
import { IIcon, IIconType } from '../../interfaces/IIcon';
import IconService from '../../services/IconService';
import { Helper } from '../../Helper';
import { IAIcon } from '../icon/Icon';
import { IAPivot } from '../pivot/Pivot';
import { LocalizationService } from '../../services/LocalizationService';
import { IASpinner } from '../spinner/Spinner';

export interface IIconPickerProps {
  selectedIcon: string;
  color: string;
  backgroundColor?: string;
  label: string;
  styles?: React.CSSProperties;
  showTitle?: boolean;
  disabled?: boolean;
  shouldBeAbleToClear: boolean;
  select: (iconUrl: string) => void;
  clear: () => void;
}

export interface IIconPickerState {
  showIconPicker: boolean;
  searchQuery: string;
  type: IIconType;
  icon?: IIcon;
  iconsToShow: number;
  isTyping: boolean;
  icons: IIcon[];
}

export class IAIconPicker extends React.Component<IIconPickerProps, IIconPickerState> {

  private typingTimer;
  private localizationService = new LocalizationService();

  constructor(props: IIconPickerProps) {
    super(props);
    this.state = {
      showIconPicker: false,
      searchQuery: '',
      type: "regular",
      iconsToShow: 40,
      isTyping: false,
      icons: []
    }
    this.localizationService?.checkLocalizedStrings().then(() => this.forceUpdate());
  }

  private onScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (event.currentTarget.offsetHeight + Math.floor(event.currentTarget.scrollTop) >= (event.currentTarget.scrollHeight - 1)) {
      this.setState({ iconsToShow: this.state.iconsToShow + 40 });
    }
  }

  private getIcons(): void {
    let icons: IIcon[] = IconService.getIcons(this.state.type, this.state.searchQuery);
    this.setState({ icons });
  }

  public render(): JSX.Element {
    const icons: JSX.Element[] = [];
    this.state.icons?.forEach((icon: IIcon, index) => {
      if (index < this.state.iconsToShow) {
        icon.type = this.state.type;
        icons.push(
          <div
            key={`icon_${icon.path}_${icon.type}`}
            className={styles.IA_iconSelectorIconToSelect}
            style={{
              backgroundColor: this.props.backgroundColor ? this.props.backgroundColor : Helper.isLightColor(this.props.color) ? "#858585" : "#f3f3f3",
            }}
            title={icon.title}
          >
            <IAIcon
              icon={icon}
              size={28}
              color={this.props.color}
              style={{
                outline: "none"
              }}
              onClick={(iconUrl: string) => {
                this.setState({ showIconPicker: false, icons: undefined, iconsToShow: 40, searchQuery: '', type: "regular" }, () => {
                  console.log(iconUrl);
                  this.props.select(iconUrl);
                  setTimeout(() => {
                    this.forceUpdate();
                  }, 100);
                });
              }}
            />
          </div>
        );
      }
    });
    return (
      <div
        className={styles.IA_iconSelector}
        style={{
          ...this.props.styles,
          opacity: this.props.disabled ? 0.5 : 1
        }}
      >
        <label>{this.props.label}</label>
        <div
          className={styles.IA_iconSelectorIcon}
          style={{
            backgroundColor: this.props.backgroundColor ? this.props.backgroundColor : Helper.isLightColor(this.props.color) ? "#858585" : "#f3f3f3",
          }}
          onClick={() => {
            if (!this.props.disabled) {
              this.setState({ showIconPicker: true, type: "regular", iconsToShow: 40 });
              this.getIcons();
            }
          }}
        >
          <IAIcon
            title={this.props.selectedIcon == undefined ? "Add" : (this.props.selectedIcon?.includes(".svg") ? undefined : this.props.selectedIcon)}
            url={this.props.selectedIcon?.includes(".svg") ? this.props.selectedIcon : undefined}
            size={24}
            color={this.props.color}
            style={{
              opacity: this.props.selectedIcon != undefined ? 1 : 0.5
            }}
          />
        </div>
        {this.props.shouldBeAbleToClear && this.props.selectedIcon != undefined &&
          <div
            className={styles.IA_iconSelectorIconDeselect}
            onClick={() => {
              this.setState({ showIconPicker: false, icons: undefined, iconsToShow: 40, type: "regular", searchQuery: '' });
              this.props.clear();
            }}
          >
            <IAIcon
              title="Dismiss"
              size={18}
              color={"gray"}
            />
          </div>
        }
        {this.state.showIconPicker &&
          <div className={styles.IA_iconSelectorWrapper}>
            <div
              className={styles.IA_iconSelectorBackground}
              onClick={() => {
                this.setState({ showIconPicker: false, icons: undefined, iconsToShow: 40, type: "regular", searchQuery: '' });
              }}
            >
            </div>
            <div className={styles.IA_iconSelectorBox}>
              <div className={styles.IA_iconSelectorBoxHeader}>
                <IAPivot
                  highlightColor={this.props.color}
                  items={[
                    {
                      key: 'regular',
                      text: this.localizationService.strings.IconPicker_Stroked,
                    },
                    {
                      key: 'filled',
                      text: this.localizationService.strings.IconPicker_Filled,
                    },
                    {
                      key: 'colored',
                      text: this.localizationService.strings.IconPicker_Colored,
                    },
                    {
                      key: 'extra',
                      text: this.localizationService.strings.IconPicker_Extra
                    }
                  ]}
                  tabBackgrondColor="#ebeaea"
                  selectedTabBackgrondColor="#ffffff"
                  selectedKey={this.state.type}
                  select={(type: "regular" | "filled" | "colored" | "extra") => {
                    this.setState({ type, iconsToShow: 40, icons: undefined }, () => this.getIcons());
                  }}
                  style={{
                    marginTop: 0,
                    height: 40,
                    marginBottom: 0,
                    marginLeft: 10
                  }}
                />
                <div className={styles.IA_iconSelectorSearchBox}
                  onClick={() => {
                    this.setState({ showIconPicker: true });
                  }}
                >
                  <input type="text" className={styles.IA_iconSelectorSearchField} placeholder={this.localizationService.strings.IconPicker_Search}
                    value={this.state.searchQuery}
                    onChange={(evt) => {
                      this.setState({ searchQuery: evt.target.value, icons: undefined });
                      if (this.state.isTyping) {
                        this.setState({ isTyping: true });
                        clearTimeout(this.typingTimer);
                        this.typingTimer = window.setTimeout(() => {
                          this.setState({ isTyping: false, iconsToShow: 40 });
                          this.getIcons();
                        }, 2000);
                      } else {
                        this.typingTimer = window.setTimeout(() => {
                          this.setState({ isTyping: false, iconsToShow: 40 });
                          this.getIcons();
                        }, 2000);
                      }
                    }} />
                  {this.state.searchQuery &&
                    <div
                      className={styles.IA_iconSelectorSearchClear}
                      onClick={() =>
                        this.setState({ searchQuery: '' }, () => this.getIcons())
                      }
                    >
                      <IAIcon
                        title="Dismiss"
                        size={18}
                        color={"gray"}
                      />
                    </div>
                  }
                </div>
              </div>
              <div
                className={styles.IA_iconSelectorBoxIcons}
                onScroll={(event) => this.onScroll(event)}
              >
                {this.state.icons ? icons : <IASpinner />}
                {this.state.searchQuery && this.state.icons != undefined && icons.length == 0 &&
                  <div className={styles.IA_iconSelectorNoContent}>
                    {this.localizationService.strings.IconPicker_NoIconsFound}
                  </div>
                }
              </div>
            </div>
          </div>
        }
      </div>
    );
  }


}